import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

type Tier = { min: number; max: number; price: string }

// Corso needs about half a second after a cart change to quote and inject.
// If nothing has arrived this long after the drawer opened, the script is
// blocked or failing and the stand-in must not keep promising protection.
const HYDRATION_GRACE_MS = 6000
const SUBTOTAL_SELECTOR = '[data-uc-cart-subtotal]'
const LIVE_WIDGET_SELECTOR = '.corso-info-container'
const PLACEHOLDER_SELECTOR = '.corso-placeholder'
const PRICE_SELECTOR = '[data-corso-placeholder-price]'

function parseTiers(raw: string): Tier[] {
	return raw
		.split('|')
		.map((chunk) => chunk.split(';'))
		.filter((parts) => parts.length === 3)
		.map(([min, max, price]) => ({
			min: Number(min),
			max: Number(max),
			price: price ?? '',
		}))
		.filter(
			(tier) =>
				Number.isFinite(tier.min) &&
				Number.isFinite(tier.max) &&
				tier.price !== ''
		)
}

// Keeps the Liquid-rendered Corso stand-in truthful between a cart change and
// Corso's own hydration: updates the tier price from the re-rendered footer's
// subtotal, and collapses the stand-in when Corso never renders.
export class CorsoCheckoutPlus extends UcoastEl {
	static htmlSelector = 'corso-checkout-plus'
	private tiers: Tier[] = []
	private observer?: MutationObserver
	private graceTimer?: number
	private scope?: HTMLElement

	override connectedCallback() {
		super.connectedCallback()
		this.tiers = parseTiers(this.dataset.tiers ?? '')
		this.scope =
			this.closest<HTMLElement>('cart-drawer') ??
			this.closest<HTMLElement>('.cart__ctas') ??
			undefined
		if (!this.scope) return
		this.observer = new MutationObserver(this.onChange)
		this.observer.observe(this.scope, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['class', 'data-uc-cart-empty'],
		})
		this.onChange()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.observer?.disconnect()
		window.clearTimeout(this.graceTimer)
	}

	private onChange = () => {
		this.syncPrice()
		this.watchHydration()
	}

	private syncPrice() {
		if (!this.scope) return
		const source = q.os<HTMLElement>(SUBTOTAL_SELECTOR, this.scope)
		const subtotal = Number.parseInt(source?.dataset.ucCartSubtotal ?? '', 10)
		if (!Number.isFinite(subtotal)) return
		const tier = this.tiers.find(
			(candidate) => subtotal >= candidate.min && subtotal <= candidate.max
		)
		const price = q.os<HTMLElement>(PRICE_SELECTOR, this.scope)
		const next = tier?.price ?? ''
		// The observer watches this subtree; an unconditional write would be a
		// new mutation on every callback and spin the page forever.
		if (price && price.textContent !== next) price.textContent = next
	}

	private watchHydration() {
		if (!this.scope) return
		const hydrated = Boolean(q.os(LIVE_WIDGET_SELECTOR, this.scope))
		const empty = this.scope.hasAttribute('data-uc-cart-empty')
		const drawerOpen =
			!this.scope.matches('cart-drawer') ||
			this.scope.classList.contains('active')
		if (hydrated || empty || !drawerOpen) {
			window.clearTimeout(this.graceTimer)
			this.graceTimer = undefined
			return
		}
		if (this.graceTimer !== undefined) return
		this.graceTimer = window.setTimeout(() => {
			this.graceTimer = undefined
			if (!this.scope || q.os(LIVE_WIDGET_SELECTOR, this.scope)) return
			q.ol<HTMLElement>(PLACEHOLDER_SELECTOR, this.scope)?.forEach(
				(placeholder) => {
					placeholder.hidden = true
				}
			)
		}, HYDRATION_GRACE_MS)
	}
}
